import type { LevelConfig } from "../level_config.js";

/**
 * Static test level: a 5-row jar pyramid (red on the ends, plum in between)
 * flanked by two stone support cubes. Baked as a plain literal instead of
 * the procedural generator this used to be — every x/y/offsetX/offsetY
 * below is just the ONE-TIME output of that generator's PACK_X/PACK_Y/
 * SUPPORT_GAP math, written out by hand so this file is pure data, same
 * shape as diamond_tower_showcase.ts's DIAMOND_TEST_LEVEL. If the packing
 * ever needs retuning (JamJar's radial_scale changes, PhysicsCube.getSize()
 * changes, etc.), regenerate these numbers rather than hand-editing them —
 * the generator script that produced them is in this session's scratchpad
 * (gen_pyramid.mjs / gen_pyramid2.mjs), not in the repo.
 *
 * Grid is 9 columns wide: columns 2-6 hold the pyramid (row 0 = apex,
 * row 4 = base), columns 1 and 7 hold the support cubes, columns 0 and 8
 * are left empty as clearance from the table's true physical edge (see
 * field_layout.ts's tableClaim — a piece dropped in the outermost column
 * sits right at that edge with near-zero margin).
 *
 * Every jar is `lying: true` (rotated to face the camera as a round
 * "candy disc" — see jam_jar.ts). offsetX packs each row's jars — and the
 * two support cubes — snugly against their neighbours instead of a full
 * grid-cell apart (JamJar's mesh is narrower than a full cell, see
 * jam_jar.ts's radial_scale); offsetY packs the rows themselves the same
 * way vertically. The support cubes' offsetX additionally accounts for a
 * cube neighbour needing less closing than a jar neighbour (a cube already
 * fills its full cell, a jar doesn't) — see the corresponding offsetX
 * values on pieces 16/17 below, smaller in magnitude than a plain jar-row
 * offset would be at the same distance from centre.
 *
 * Each piece's `x` is its own row's nearest whole column to where it's
 * actually packed (not just "count columns starting at COL_SHIFT" — that
 * left every row but the base one sitting far from its real, packed
 * position, which meant a correspondingly huge offsetX to drag it back —
 * the apex jar alone needed offsetX: 2, tripping validateLevelLayer's
 * MAX_ABS_OFFSET=1 warning for no real reason). Picking the nearest column
 * per piece keeps every offsetX here within about ±0.6 — genuinely just the
 * fractional "pack tighter than a full cell" nudge described above, nothing
 * more.
 */
const JAR_PYRAMID_LEVEL: LevelConfig = {
  level: 90301,
  difficulty: 0 as LevelConfig["difficulty"], // same numeric value as PlayDialogDifficulty.Common
  layers: [
    {
      grid: [
        [ 0, 0, 0, 0, 1, 0, 0, 0, 0 ],
        [ 0, 0, 0, 0, 2, 3, 0, 0, 0 ],
        [ 0, 0, 0, 4, 5, 6, 0, 0, 0 ],
        [ 0, 0, 0, 7, 8, 9, 10, 0, 0 ],
        [ 0, 16, 11, 12, 13, 14, 15, 17, 0 ],
      ],
      pieces: [
        // Row 0 — apex (1 jar).
        { id: 1, type: 3, x: 4, y: 0, width: 1, height: 1, variant: 0, offsetX: 0, offsetY: -0.4, rotationDegX: 90 },

        // Row 1 (2 jars).
        { id: 2, type: 3, x: 4, y: 1, width: 1, height: 1, variant: 0, offsetX: -0.46, offsetY: -0.3, rotationDegX: 90 },
        { id: 3, type: 3, x: 5, y: 1, width: 1, height: 1, variant: 0, offsetX: -0.54, offsetY: -0.3, rotationDegX: 90 },

        // Row 2 (3 jars).
        { id: 4, type: 3, x: 3, y: 2, width: 1, height: 1, variant: 0, offsetX: 0.08, offsetY: -0.2, rotationDegX: 90 },
        { id: 5, type: 3, x: 4, y: 2, width: 1, height: 1, variant: 1, offsetX: 0, offsetY: -0.2, rotationDegX: 90 },
        { id: 6, type: 3, x: 5, y: 2, width: 1, height: 1, variant: 0, offsetX: -0.08, offsetY: -0.2, rotationDegX: 90 },

        // Row 3 (4 jars).
        { id: 7, type: 3, x: 3, y: 3, width: 1, height: 1, variant: 0, offsetX: -0.38, offsetY: -0.1, rotationDegX: 90 },
        { id: 8, type: 3, x: 4, y: 3, width: 1, height: 1, variant: 1, offsetX: -0.46, offsetY: -0.1, rotationDegX: 90 },
        { id: 9, type: 3, x: 5, y: 3, width: 1, height: 1, variant: 1, offsetX: -0.54, offsetY: -0.1, rotationDegX: 90 },
        { id: 10, type: 3, x: 6, y: 3, width: 1, height: 1, variant: 0, offsetX: -0.62, offsetY: -0.1, rotationDegX: 90 },

        // Row 4 — base (5 jars) + the two support cubes flanking it.
        { id: 11, type: 3, x: 2, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.16, offsetY: 0, rotationDegX: 90 },
        { id: 12, type: 3, x: 3, y: 4, width: 1, height: 1, variant: 1, offsetX: 0.08, offsetY: 0, rotationDegX: 90 },
        { id: 13, type: 3, x: 4, y: 4, width: 1, height: 1, variant: 1, offsetX: 0, offsetY: 0, rotationDegX: 90 },
        { id: 14, type: 3, x: 5, y: 4, width: 1, height: 1, variant: 1, offsetX: -0.08, offsetY: 0, rotationDegX: 90 },
        { id: 15, type: 3, x: 6, y: 4, width: 1, height: 1, variant: 0, offsetX: -0.16, offsetY: 0, rotationDegX: 90 },
        { id: 16, type: 2, x: 1, y: 4, width: 1, height: 1, variant: 0, offsetX: 0.18 },
        { id: 17, type: 2, x: 7, y: 4, width: 1, height: 1, variant: 0, offsetX: -0.18 },
      ],
    },
  ],
};

export class JarPyramidShowcase
{
  public static readonly LEVEL = JAR_PYRAMID_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? JAR_PYRAMID_LEVEL : null;
  }
}
